import React, { useMemo } from 'react';
import { LFC } from '../../../Legacy';
import { Ticker } from '../../../concepts/objects/ObjectSymbols';
import { Icon } from '../../../concepts/resources/Icon';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { RelativeTo } from '../../canvas/entities/Physical';
import { ComponentMount } from '../../canvas/visualization/ComponentMount';
import { RenderBounds, RenderBox, RenderInner, RenderSvg, usePose } from '../../canvas/visualization/RenderHelpers';
import { RenderText } from '../../canvas/visualization/RenderText';
import { Frame } from './Frame';
import { FrameAddon } from './FrameAddon';
import { FrameBorderWidthAttr } from './FrameAssociations';
import { FrameTitleFieldTextSettings } from './FrameTitleField';
import { ActivityFrameAddon } from './addons/ActivityFrameAddon';
import { ActivityFrameAddonVisual } from './addons/ActivityFrameAddonVisual';
import { CalendarFrameAddon } from './addons/CalendarFrameAddon';
import { CalendarFrameAddonVisual } from './addons/CalendarFrameAddonVisual';
import { GridFrameAddon } from './addons/GridFrameAddon';
import { GridFrameAddonVisual } from './addons/GridFrameAddonVisual';
import { TimelineFrameAddon } from './addons/TimelineFrameAddon';
import { TimelineFrameAddonVisual } from './addons/TimelineFrameAddonVisual';

const icons = {
    'default': 'deqo://icons/d2/frame',
    'ActivityFrameAddon': 'deqo://icons/d2/frame-activity',
    'GridFrameAddon': 'deqo://icons/d2/frame-grid',
    'TimelineFrameAddon': 'deqo://icons/d2/frame-timeline',
};

const RenderBorder = React.memo<{ area: Rect, stroke: string, strokeWidth: number }>(({ area, stroke, strokeWidth }) => {
    // const { base, badge } = object.layout;
    // const { selected, interaction } = usePose(object);
    // const highlight = !selected && interaction?.type === 'select';
    // const stroke = highlight ? Colors.primaryLight : Colors.frame;

    const extra = area.inflate(10);
    const offset = extra.topLeft().multiply(-1);// .add(strokeWidth / 2);

    return <RenderSvg area={extra}>
        <g transform={`translate(${offset.format('svg')})`}>
            {/* <rect rx={strokeWidth * 2} {...badge.toObject('xy')} fill="#F6FAFD" stroke={Colors.frame} strokeWidth={strokeWidth} /> */}
            <rect rx={strokeWidth * 4} {...area.toObject('xy')} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
        </g>
    </RenderSvg>;
});

const RenderIcon: LFC<{ object: Frame, type: string }> = ({ object, type }) => {
    const { title } = object.layout;
    const style = {
        position: 'absolute' as const,
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: title.height * 1,
        height: title.height * 1,
        color: Colors.textLight,
    };
    return <RenderBox {...title.toCSS()}>
        <Icon style={style} src={icons[type] ?? icons.default} />
    </RenderBox>;
};

const RenderBackground: LFC<{ object: Frame }> = ({ object }) => {
    if (!object.background || object.background === 'transparent') return null;
    return <RenderBox inset="0" background={object.background} />;
};

const RenderAddon: LFC<{ object: FrameAddon }> = ({ object }) => <>
    {object.is(ActivityFrameAddon) && <ComponentMount object={object} visual={ActivityFrameAddonVisual} />}
    {object.is(CalendarFrameAddon) && <ComponentMount object={object} visual={CalendarFrameAddonVisual} />}
    {object.is(GridFrameAddon) && <ComponentMount object={object} visual={GridFrameAddonVisual} />}
    {object.is(TimelineFrameAddon) && <ComponentMount object={object} visual={TimelineFrameAddonVisual} />}
</>;

const customizeText = (props: any) => ({
    ...props,
    display: 'block',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
});

export const FrameVisual: LFC<{ object: Frame }> = ({ object, children }) => {

    const addon = useMemo(() => object.addon(), [object[Ticker]]);
    const bounds = object.bounds(RelativeTo.Model);
    const { layout } = object;
    const { beingEdited, selected, interaction, targeted } = usePose(object);
    const highlight = !selected && interaction?.type === 'select';

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderBorder area={layout.base} stroke={highlight ? Colors.primaryLight : Colors.frame} strokeWidth={FrameBorderWidthAttr.get(object)} />
                <RenderBackground object={object} />
                {addon && addon.layer === 'bottom' && <RenderAddon object={addon} />}
            </RenderInner>
        </RenderBounds>
        {children}
        <RenderBox {...bounds.toCSS()} zIndex={10}>
            {addon && addon.layer === 'top' && <RenderAddon object={addon} />}
            {addon && <RenderIcon object={object} type={addon.type} />}
            {!beingEdited && <RenderText bounds={layout.title} settings={FrameTitleFieldTextSettings(object)} value={object.title} customize={customizeText} />}
        </RenderBox>
    </>;
};
